import React, { useState, useEffect } from "react";
import {
  getRadius,
  getFontSize,
  getInputPadding,
  getHeight,
} from "../utils/cssVariableMapper";
import Swal from "sweetalert2";

const FormField = ({
  field,
  config,
  onMove,
  onDelete,
  onToggleRequired,
  isFirst,
  isLast,
  isPreview,
  textColor,
  isSelected,
  onSelect,
  onAnswerChange, // 👈 NEW PROP: To sync data to parent
  isHidden = false,
}) => {
  // Styles
  const radiusClass = getRadius(config?.radius || "md");
  const textClass = getFontSize(config?.inputTextSize || "md");
  const labelClass = getFontSize(config?.labelSize || "sm");
  const paddingClass = getInputPadding(config?.padding || "md");
  const heightClass = getHeight(config?.height || "md");
  const isUnderlined = config?.variant === "underlined";
  const isFilled = config?.variant === "filled";

  // Grid Layout
  const widthClass =
    field.width === "half" ? "col-span-1" : "col-span-1 md:col-span-2";

  // ----------------------------------------------------
  // 🧠 STATE MANAGEMENT
  // ----------------------------------------------------

  const [value, setValue] = useState(field.type === "checkbox" ? [] : "");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setValue(field.type === "checkbox" ? [] : "");
    setError("");
    setTouched(false);
    setStartDate("");
    setEndDate("");
  }, [field.id, isPreview]);

  // 🔍 VALIDATION LOGIC
  const validate = (val) => {
    // 1. Check Required
    if (field.required) {
      // 📅 Special Logic for Date Range
      if (field.type === "date" && field.dateMode === "range") {
        if (!startDate || !endDate) return "This field is required.";
      }
      // 📝 Logic for Standard Inputs
      else {
        const isEmpty =
          val === "" ||
          val === null ||
          (Array.isArray(val) && val.length === 0);

        if (isEmpty) return "This field is required.";
      }
    }

    // 2. Check Email Format
    if (field.type === "email" && val) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(val)) return "Please enter a valid email address.";
    }

    // 3. Check Date Logic (Start vs End)
    if (field.type === "date" && field.dateMode === "range") {
      if (startDate && endDate && startDate > endDate) {
        return "End date cannot be before start date.";
      }
    }

    return ""; // No error
  };

  // ✍️ HANDLE CHANGE (Standard Inputs)
  const handleChange = (val) => {
    setValue(val);

    // ✅ SYNC TO PARENT
    if (onAnswerChange) onAnswerChange(field.id, val);

    if (touched) {
      setError(validate(val));
    }
  };

  // ☑️ CHECKBOX HANDLER
  const handleCheckboxChange = (option) => {
    let newValues = [...(value || [])];
    if (newValues.includes(option)) {
      newValues = newValues.filter((v) => v !== option);
    } else {
      newValues.push(option);
    }
    setValue(newValues);

    // ✅ SYNC TO PARENT
    if (onAnswerChange) onAnswerChange(field.id, newValues);

    if (touched) setError(validate(newValues));
  };

  // 📅 DATE RANGE HANDLER
  const handleDateChange = (type, val) => {
    // Determine new state values immediately for validation
    let newStart = startDate;
    let newEnd = endDate;

    if (type === "start") {
      setStartDate(val);
      newStart = val;
      // Auto-clear end date if invalid
      if (endDate && val > endDate) {
        setEndDate("");
        newEnd = "";
      }
    } else {
      setEndDate(val);
      newEnd = val;
    }

    // ✅ SYNC TO PARENT (Send object for range)
    if (onAnswerChange) {
      onAnswerChange(field.id, { start: newStart, end: newEnd });
    }

    // Real-time validation using the NEW values
    if (touched && field.required) {
      if (!newStart || !newEnd) setError("This field is required.");
      else setError("");
    }
  };

  // 🖱️ HANDLE BLUR (Focus Out)
  const handleBlur = () => {
    setTouched(true);
    setError(validate(value));
  };

  // ----------------------------------------------------
  // 🎨 STYLING
  // ----------------------------------------------------

  let baseStyle = `w-full transition-all duration-200 focus:outline-none focus:ring-2 disabled:bg-transparent ${textClass} text-gray-700 placeholder-gray-400`;
  const borderColor = error
    ? "border-red-500 focus:border-red-500 focus:ring-red-200"
    : "focus:ring-blue-500/20";

  if (isUnderlined) {
    baseStyle += ` border-b ${
      error ? "border-red-500" : "border-gray-300"
    } bg-transparent px-0 rounded-none focus:border-blue-600 ${borderColor}`;
  } else {
    baseStyle += ` border ${radiusClass} ${paddingClass} ${borderColor}`;
    if (isFilled) {
      baseStyle += ` border-transparent bg-gray-100 focus:bg-white ${
        error ? "border-red-500" : "focus:border-gray-300"
      }`;
    } else {
      baseStyle += ` ${
        error ? "border-red-500" : "border-gray-300"
      } bg-white hover:border-gray-400`;
    }
  }

  const inputClasses = `${baseStyle} ${heightClass}`;
  const textareaClasses = `${baseStyle} py-3`;

  const containerClasses = `${isHidden ? "hidden " : ""}${
    isPreview
      ? `mb-1 ${widthClass}`
      : `relative group p-4 -mx-4 rounded-xl transition-all cursor-pointer border-2 ${
          isSelected
            ? "border-blue-500 bg-blue-50/10"
            : "border-transparent hover:bg-gray-50"
        } ${widthClass}`
  }`;

  return (
    <div
      className={containerClasses}
      aria-hidden={isHidden || undefined}
      onClick={(e) => {
        if (!isPreview) {
          e.stopPropagation();
          onSelect();
        }
      }}
    >
      {/* Edit Overlay */}
      {!isPreview && isSelected && (
        <div className="absolute right-2 -top-3 flex items-center gap-1 bg-white shadow-md border border-gray-200 rounded-lg p-1 z-20">
          <button
            onClick={() => onMove("up")}
            disabled={isFirst}
            className="p-1 hover:bg-gray-100 rounded text-gray-500 disabled:opacity-30"
          >
            <span className="material-icons text-sm">arrow_upward</span>
          </button>
          <button
            onClick={() => onMove("down")}
            disabled={isLast}
            className="p-1 hover:bg-gray-100 rounded text-gray-500 disabled:opacity-30"
          >
            <span className="material-icons text-sm">arrow_downward</span>
          </button>
          <div className="w-px h-3 bg-gray-200 mx-1"></div>
          <button
            onClick={onDelete}
            className="p-1 hover:bg-red-50 text-red-500 rounded"
          >
            <span className="material-icons text-sm">delete</span>
          </button>
        </div>
      )}

      {/* Label */}
      <label
        className={`block font-semibold ${labelClass} mb-2`}
        style={{ color: textColor || "#374151" }}
      >
        {field.label}{" "}
        {field.required && <span className="text-red-500">*</span>}
      </label>

      {/* --- FIELD RENDERERS --- */}

      {field.type === "textarea" && (
        <textarea
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          placeholder={field.placeholder}
          className={`${textareaClasses} min-h-[100px] resize-none`}
        />
      )}

      {(field.type === "text" ||
        field.type === "email" ||
        field.type === "number") && (
        <input
          type={field.type}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          placeholder={field.placeholder}
          className={inputClasses}
        />
      )}

      {field.type === "select" && (
        <div className="relative">
          <select
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={handleBlur}
            className={`${inputClasses} appearance-none cursor-pointer`}
          >
            <option value="">{field.placeholder || "Select an option"}</option>
            {field.options?.map((opt, i) => (
              <option key={i} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <span className="material-icons absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-sm">
            expand_more
          </span>
        </div>
      )}

      {/* DATE PICKER (Single vs Range) */}
      {field.type === "date" && (
        <div className="w-full">
          {field.dateMode === "range" ? (
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  type="date"
                  className={`${inputClasses} px-2 w-full text-sm text-gray-500`}
                  value={startDate}
                  onChange={(e) => handleDateChange("start", e.target.value)}
                  onBlur={handleBlur}
                  max={endDate}
                  onClick={(e) => e.target.showPicker && e.target.showPicker()}
                />
              </div>
              <span className="text-gray-400 font-bold">→</span>
              <div className="relative flex-1">
                <input
                  type="date"
                  className={`${inputClasses} px-2 w-full text-sm text-gray-500 ${
                    !startDate ? "opacity-50 cursor-not-allowed bg-gray-50" : ""
                  }`}
                  value={endDate}
                  onChange={(e) => handleDateChange("end", e.target.value)}
                  onBlur={handleBlur}
                  min={startDate}
                  disabled={!startDate}
                  onClick={(e) => e.target.showPicker && e.target.showPicker()}
                />
              </div>
            </div>
          ) : (
            // Single Date
            <div className="relative">
              <input
                type="date"
                value={value}
                onChange={(e) => handleChange(e.target.value)}
                onBlur={handleBlur}
                className={`${inputClasses} px-2 w-full text-sm text-gray-500`}
                onClick={(e) => e.target.showPicker && e.target.showPicker()}
              />
            </div>
          )}
        </div>
      )}

      {/* TIME PICKER */}
      {field.type === "time" && (
        <div className="relative">
          <input
            type="time"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={handleBlur}
            className={`${inputClasses} px-2`}
          />
        </div>
      )}

      {/* RATING */}
      {field.type === "rating" && (
        <div className="flex gap-1" onBlur={handleBlur}>
          {Array.from({ length: field.maxStars || 5 }).map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => handleChange(i + 1)}
              className="focus:outline-none transition-transform active:scale-110"
            >
              <span
                className={`material-icons text-3xl transition-colors ${
                  (value || 0) > i
                    ? "text-yellow-400"
                    : "text-gray-300 hover:text-yellow-200"
                }`}
              >
                star_rate
              </span>
            </button>
          ))}
        </div>
      )}

      {field.type === "switch" && (
        <div
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => handleChange(!value)}
        >
          <div
            className={`w-12 h-6 rounded-full relative transition-colors duration-300 ${
              value ? "bg-blue-600" : "bg-gray-200"
            }`}
          >
            <div
              className={`w-5 h-5 bg-white rounded-full absolute top-0.5 shadow-sm transition-all duration-300 ${
                value ? "left-[26px]" : "left-0.5"
              }`}
            ></div>
          </div>
          <span className="text-sm text-gray-500">{field.label}</span>
        </div>
      )}

      {/* RADIO BUTTONS */}
      {field.type === "radio" && (
        <div className="space-y-2">
          {field.options?.map((opt, i) => (
            <label
              key={i}
              className="flex items-center gap-2 cursor-pointer group"
            >
              <div
                className={`w-4 h-4 rounded-full border flex items-center justify-center transition-all ${
                  value === opt
                    ? "border-blue-500"
                    : "border-gray-300 group-hover:border-blue-400"
                }`}
              >
                {value === opt && (
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                )}
              </div>
              <input
                type="radio"
                name={field.id}
                value={opt}
                checked={value === opt}
                onChange={(e) => handleChange(e.target.value)}
                onBlur={handleBlur}
                className="hidden"
              />
              <span className="text-sm text-gray-600">{opt}</span>
            </label>
          ))}
        </div>
      )}

      {/* CHECKBOXES */}
      {field.type === "checkbox" && (
        <div className="space-y-2">
          {field.options?.map((opt, i) => (
            <label
              key={i}
              className="flex items-center gap-2 cursor-pointer group"
            >
              <div
                className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${
                  value.includes(opt)
                    ? "bg-blue-500 border-blue-500"
                    : "border-gray-300 group-hover:border-blue-400"
                }`}
              >
                {value.includes(opt) && (
                  <span className="material-icons text-white text-[10px] font-bold">
                    check
                  </span>
                )}
              </div>
              <input
                type="checkbox"
                value={opt}
                checked={value.includes(opt)}
                onChange={() => handleCheckboxChange(opt)}
                onBlur={handleBlur}
                className="hidden"
              />
              <span className="text-sm text-gray-600">{opt}</span>
            </label>
          ))}
        </div>
      )}

      {field.type === "file" && (
        <div className="relative">
          <input
            type="file"
            className="hidden"
            id={`file-${field.id}`}
            onChange={(e) => {
              const file = e.target.files[0];
              if (file.size > 2 * 1024 * 1024) {
                Swal.fire({
                  icon: "error",
                  title: "File too large",
                  text: "Please upload a file smaller than 2 MB.",
                  confirmButtonText: "OK",
                });
              }
              handleChange(file);
            }}
            onBlur={handleBlur}
          />
          <label
            htmlFor={`file-${field.id}`}
            className={`w-full border-2 border-dashed ${
              error ? "border-red-400 bg-red-50" : "border-gray-300 bg-gray-50"
            } hover:bg-white rounded-lg p-6 flex flex-col items-center justify-center text-gray-400 cursor-pointer transition-all`}
          >
            {value ? (
              <>
                <span className="material-icons text-3xl mb-1 text-blue-500">
                  description
                </span>
                {/* ✅ FIX: Handle File Object display */}
                <span className="text-sm text-gray-800 font-medium">
                  {value instanceof File ? value.name : value}
                </span>
                <span className="text-xs text-blue-500 mt-1">
                  Click to replace
                </span>
              </>
            ) : (
              <>
                <span className="material-icons text-3xl mb-1">
                  cloud_upload
                </span>
                <span className="text-xs">Drag & drop or click to upload</span>
              </>
            )}
          </label>
        </div>
      )}

      {/* 🛑 ERROR MESSAGE */}
      {error && (
        <div className="flex items-center gap-1 mt-1.5 animate-fade-in">
          <span className="material-icons text-red-500 text-[14px]">
            error_outline
          </span>
          <p className="text-xs text-red-500 font-medium">{error}</p>
        </div>
      )}
    </div>
  );
};

export default FormField;
